import { Component, signal, computed, inject, effect, untracked, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ButtonComponent } from '../../../shared/components/ui/button/button.component';
import { DropdownComponent, DropdownOption } from '../../../shared/components/ui/dropdown/dropdown.component';
import { IconComponent } from '../../../shared/icons/icon.component';
import { PaginatedTableComponent, TableHeader, TableActionOptions } from '../../../shared/components/tables/paginated-table/paginated-table.component';
import { SlidePanelComponent } from '../../../shared/components/slide-panel/slide-panel.component';
import { InputComponent } from '../../../shared/components/ui/input/input.component';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators, FormArray } from '@angular/forms';
import { AiBotService } from '../../../core/services/ai-bot.service';
import { ApiService } from '../../../core/services/api.service';
import { ToasterService } from '../../../core/services/toaster.service';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmationModalComponent } from '../../../shared/components/confirmation-modal/confirmation-modal.component';
import { AuditService } from '../../../core/services/audit.service';
import { AuditAction, AuditEntityType } from '../../../core/models/audit-log.model';
import { AuditLogPanelComponent } from '../../../shared/components/audit-log-panel/audit-log-panel.component';
import { PromptBotService } from '../../../core/services/prompt-bot.service';
import { Feature, FeatureOption } from '../../../core/models/prompt-bot.model';
import { environment } from '../../../../environments/environment';

@Component({
    selector: 'app-ai-summary-detail',
    standalone: true,
    imports: [
        CommonModule,
        ButtonComponent,
        DropdownComponent,
        IconComponent,
        PaginatedTableComponent,
        SlidePanelComponent,
        InputComponent,
        ReactiveFormsModule,
        AuditLogPanelComponent
    ],
    templateUrl: './ai-summary-detail.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styles: []
})
export class AiSummaryDetailComponent {
    @ViewChild(AuditLogPanelComponent) auditLogPanel!: AuditLogPanelComponent;
    private fb = inject(FormBuilder);
    private aiBotService = inject(AiBotService);
    private apiService = inject(ApiService);
    private toasterService = inject(ToasterService);
    private dialog = inject(MatDialog);
    private auditService = inject(AuditService);
    private promptBotService = inject(PromptBotService);

    /** Previous state snapshots for UPDATE diff logging */
    private previousHeadlineState: any = null;
    private previousApiState: any = null;
    private previousAuthState: any = null;

    activeTab = signal<'headline' | 'api' | 'auth' | 'agent'>('headline');
    auditEntityTypes = [AuditEntityType.HEADLINE_PAGE, AuditEntityType.HEADLINE_API, AuditEntityType.HEADLINE_AUTH];
    panelOpen = signal(false);
    isEditMode = signal(false);
    editingId = signal<number | null>(null);

    // Filter Signals
    searchQuery = signal('');
    selectedStatus = signal<string>('all');
    selectedMethod = signal<string>('all');
    selectedPage = signal<string>('all');
    selectedProduct = signal<number | 'all'>('all');
    // Applied (committed) filters - update only when Apply is clicked
    appliedSearch = signal('');
    appliedStatus = signal<string>('all');
    appliedMethod = signal<string>('all');
    appliedPage = signal<string>('all');
    appliedProduct = signal<number | 'all'>('all');

    apiForm: FormGroup = this.fb.group({
        id: [0, [Validators.required]],
        product_id: [null],
        headline_page_id: [null],
        data_source_id: [null],
        name: ['', [Validators.required]],
        keyname: ['', [Validators.required]],
        description: [''],
        method: ['GET'],
        endpoint: [''],
        payload: this.fb.array([])
    });

    agentForm: FormGroup = this.fb.group({
        product_id: [null, [Validators.required]],
        feature_id: [null, [Validators.required]],
        feature_option_id: [null, [Validators.required]],
        page_ids: [[]],
        api_ids: [[]],
        name: ['', [Validators.required]],
        knowledge_base_collection: ['']
    });

    get payloadArray() {
        return this.apiForm.get('payload') as FormArray;
    }

    // Headline Pages Data
    headlineHeaders: TableHeader[] = [
        { label: 'PRODUCT', key: 'product_name', sortable: true },
        { label: 'NAME', key: 'name', sortable: true },
        { label: 'KEYNAME', key: 'keyname', sortable: true },
        { label: 'DESCRIPTION', key: 'formatted_description', isHtml: true },
        { label: 'STATUS', key: 'formatted_status', isHtml: true },
        { label: 'CREATED AT', key: 'formatted_created_at', sortable: true }
    ];

    headlineData = signal<any[]>([]);

    private currentPid = computed(() => {
        const productId = this.apiService.selectedProductId();
        const products = this.apiService.products();
        const product = products.find(p => p.id === productId);
        return product?.pid || productId;
    });

    constructor() {
        effect(() => {
            untracked(() => {
                this.fetchHeadlinePages();
                this.fetchAuthData();
                this.fetchProducts();
                this.fetchAllFeatures();
            });
        });
    }

    private fetchHeadlinePages(productId?: number) {
        this.aiBotService.getHeadlinePages(productId).subscribe({
            next: (res: any) => {
                if (res && res.data && Array.isArray(res.data)) {
                    this.headlineData.set(res.data);
                } else if (Array.isArray(res)) {
                    this.headlineData.set(res);
                }
            },
            error: (err) => {
                console.error('Error fetching headline pages:', err);
            }
        });
    }

    // API Endpoints Data
    apiHeaders: TableHeader[] = [
        { label: 'PRODUCT', key: 'product_name', sortable: true },
        { label: 'NAME', key: 'api_name', sortable: true },
        { label: 'ENDPOINT', key: 'base_url', sortable: true },
        { label: 'DESCRIPTION', key: 'formatted_description', isHtml: true },
        { label: 'METHOD', key: 'method', align: 'text-center' },
        { label: 'STATUS', key: 'formatted_status', isHtml: true },
        { label: 'CREATED AT', key: 'formatted_created_at', sortable: true }
    ];

    apiData = signal<any[]>([]);

    // Agent Management Data
    agentHeaders: TableHeader[] = [
        { label: 'NAME', key: 'name', sortable: true },
        { label: 'PRODUCT', key: 'product_name', sortable: true },
        { label: 'FEATURE', key: 'feature_name', sortable: true },
        { label: 'PAGE', key: 'feature_option_name', sortable: true }
    ];

    agentData = signal<any[]>([]);

    // Mapping signals for agent listing names
    allFeatures = signal<Feature[]>([]);
    allFeatureOptionsMap = signal<Record<number, FeatureOption[]>>({});

    // Agent Management Signals
    agentPanelOpen = signal(false);
    agentIsEditMode = signal(false);
    agentEditingId = signal<number | null>(null);
    features = signal<Feature[]>([]);
    featureOptions = signal<FeatureOption[]>([]);

    // Computed data with classes for the table component
    headlineDropdownOptions = computed<DropdownOption[]>(() => {
        return this.headlineData().map(item => ({
            label: item.name || 'Untitled Page',
            value: item.id
        }));
    });

    pageFilterOptions = computed<DropdownOption[]>(() => {
        const options: DropdownOption[] = [
            { label: 'All Pages', value: 'all' }
        ];
        const pageOptions = this.headlineData().map(item => ({
            label: item.name || 'Untitled Page',
            value: String(item.id || '')
        }));
        return [...options, ...pageOptions];
    });

    authDropdownOptions = computed<DropdownOption[]>(() => {
        return this.authData().map(item => ({
            label: item.name || 'Untitled Auth',
            value: item.id
        }));
    });

    productData = signal<any[]>([]);
    productDropdownOptions = computed<DropdownOption[]>(() => {
        const options: DropdownOption[] = [
            { label: 'All Products', value: 'all' }
        ];
        const productOptions = this.productData().map(item => ({
            label: item.name || 'Untitled Product',
            value: String(item.id || '')
        }));
        return [...options, ...productOptions];
    });

    agentProductDropdownOptions = computed<DropdownOption[]>(() => {
        return this.productData().map(item => ({
            label: item.name || 'Untitled Product',
            value: String(item.id || ''),
            disabled: item.name !== 'Denefits'
        }));
    });

    agentFeatureDropdownOptions = computed<DropdownOption[]>(() => {
        return this.features()
            .map(item => ({
                label: item.name || 'Untitled Feature',
                value: String(item.id || ''),
                disabled: (item.name || '').toLowerCase() !== 'ask prism ai'
            }))
            .sort((a, b) => (a.disabled === b.disabled ? 0 : a.disabled ? 1 : -1));
    });

    agentFeatureOptionDropdownOptions = computed<DropdownOption[]>(() => {
        return this.featureOptions().map(item => ({
            label: item.name || 'Untitled Page',
            value: String(item.id || '')
        }));
    });

    agentHeadlinePageDropdownOptions = computed<DropdownOption[]>(() => {
        return this.headlineData().map(item => ({
            label: item.name || 'Untitled Page',
            value: String(item.id || '')
        }));
    });

    agentApiDropdownOptions = computed<DropdownOption[]>(() => {
        return this.apiData().map(item => ({
            label: item.api_name || 'Untitled API',
            value: String(item.id || '')
        }));
    });

    // Filtered Headline Data
    filteredHeadlineData = computed(() => {
        const search = this.appliedSearch().toLowerCase();
        const status = this.appliedStatus();
        const product = this.appliedProduct();
        return this.processedHeadlineData().filter(item => {
            const matchSearch = !search ||
                (item.name || '').toLowerCase().includes(search) ||
                (item.keyname || '').toLowerCase().includes(search) ||
                (item.description || '').toLowerCase().includes(search);
            const isActive = item.is_active === true || item.is_active === 1;
            const matchStatus = status === 'all' || (status === 'active' ? isActive : !isActive);
            const matchProduct = product === 'all' || item.product_id === product;
            return matchSearch && matchStatus && matchProduct;
        });
    });

    // Filtered Auth Data
    filteredAuthData = computed(() => {
        const search = this.appliedSearch().toLowerCase();
        const status = this.appliedStatus();
        const product = this.appliedProduct();
        return this.processedAuthData().filter(item => {
            const matchSearch = !search ||
                (item.name || '').toLowerCase().includes(search) ||
                (item.base_url || '').toLowerCase().includes(search) ||
                (item.auth_type || '').toLowerCase().includes(search);
            const isActive = item.is_active === true || item.is_active === 1;
            const matchStatus = status === 'all' || (status === 'active' ? isActive : !isActive);
            const matchProduct = product === 'all' || item.product_id === product;
            return matchSearch && matchStatus && matchProduct;
        });
    });

    // Filtered API Data
    filteredApiData = computed(() => {
        const search = this.appliedSearch().toLowerCase();
        const status = this.appliedStatus();
        const method = this.appliedMethod();
        const page = this.appliedPage();
        const product = this.appliedProduct();
        return this.processedApiData().filter(item => {
            const matchSearch = !search ||
                (item.api_name || '').toLowerCase().includes(search) ||
                (item.base_url || '').toLowerCase().includes(search) ||
                (item.method || '').toLowerCase().includes(search);
            const isActive = item.is_active === true || item.is_active === 1;
            const matchStatus = status === 'all' || (status === 'active' ? isActive : !isActive);
            const matchMethod = method === 'all' || (item.method || '').toUpperCase() === method.toUpperCase();
            const matchPage = page === 'all' || String(item.headline_page_id) === String(page);
            const matchProduct = product === 'all' || String(item.resolved_product_id) === String(product);
            return matchSearch && matchStatus && matchMethod && matchPage && matchProduct;
        });
    });

    processedHeadlineData = computed(() => {
        const products = this.productData();
        return this.headlineData().map(item => {
            const isActive = item.is_active === true || item.is_active === 1;
            const statusColor = isActive
                ? 'bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-400'
                : 'bg-slate-100 text-slate-600 dark:bg-slate-500/20 dark:text-slate-400';
            const createdAt = item.created_at
                ? new Date(item.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                : '—';

            const description = item.description || '—';
            const formattedDescription = `<div class="max-w-[200px] truncate" title="${description}">${description}</div>`;

            const product = products.find((p: any) => p.id === item.product_id || String(p.id) === String(item.product_id));
            const product_name = product?.name || '—';

            return {
                ...item,
                product_name,
                formatted_status: `<span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${statusColor}">${isActive ? 'Active' : 'Inactive'}</span>`,
                formatted_created_at: createdAt,
                formatted_description: formattedDescription
            };
        });
    });

    processedApiData = computed(() => {
        const products = this.productData();
        const headlines = this.headlineData();
        return this.apiData().map(item => {
            const isActive = item.is_active === true || item.is_active === 1;
            const statusColor = isActive
                ? 'bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-400'
                : 'bg-slate-100 text-slate-600 dark:bg-slate-500/20 dark:text-slate-400';
            const createdAt = item.created_at
                ? new Date(item.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                : '—';

            const description = item.description || '—';
            const formattedDescription = `<div class="max-w-[300px] truncate" title="${description}">${description}</div>`;

            // Resolve product via linked headline page first, then directly on item
            const linkedPage = headlines.find((h: any) => h.id === item.headline_page_id);
            const resolved_product_id = linkedPage?.product_id ?? item.product_id ?? null;
            const product = products.find((p: any) => p.id === resolved_product_id || String(p.id) === String(resolved_product_id));
            const product_name = product?.name || '—';

            return {
                ...item,
                product_name,
                resolved_product_id,
                formatted_status: `<span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${statusColor}">${isActive ? 'Active' : 'Inactive'}</span>`,
                formatted_created_at: createdAt,
                formatted_description: formattedDescription
            };
        });
    });

    processedAgentData = computed(() => {
        const products = this.productData();
        const features = this.allFeatures();
        const optionsMap = this.allFeatureOptionsMap();

        return this.agentData().map(agent => {
            const product = products.find(p => p.id === agent.product_id || p.pid === agent.product_id);
            const feature = features.find(f => f.id === agent.feature_id);
            const options = optionsMap[agent.feature_id] || [];
            const option = options.find(o => o.id === agent.feature_option_id);

            return {
                ...agent,
                product_name: product?.name || agent.product_id || '—',
                feature_name: feature?.name || agent.feature_id || '—',
                feature_option_name: option?.name || agent.feature_option_id || '—'
            };
        });
    });

    // Authentication Data
    authHeaders: TableHeader[] = [
        { label: 'NAME', key: 'name', sortable: true },
        { label: 'ENVIRONMENT', key: 'environment' },
        { label: 'BASE URL', key: 'base_url' },
        { label: 'AUTH TYPE', key: 'auth_type' },
        { label: 'STATUS', key: 'formatted_status', isHtml: true }
    ];

    authData = signal<any[]>([]);

    processedAuthData = computed(() => {
        return this.authData().map(item => {
            const isActive = item.is_active === true || item.is_active === 1;
            const statusColor = isActive
                ? 'bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-400'
                : 'bg-slate-100 text-slate-600 dark:bg-slate-500/20 dark:text-slate-400';
            const createdAt = item.created_at
                ? new Date(item.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                : '—';

            return {
                ...item,
                formatted_status: `<span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${statusColor}">${isActive ? 'Active' : 'Inactive'}</span>`,
                formatted_created_at: createdAt
            };
        });
    });

    authActionOptions: TableActionOptions = {
        showToggleButton: true,
        columnLabel: 'ACTIONS',
        toggleOptions: [
            { label: 'Edit', iconName: 'pencil', value: 'edit', color: 'text-blue-500', isShow: true },
            { label: 'Delete', iconName: 'trash', value: 'delete', color: 'text-red-500', isShow: true }
        ]
    };

    authForm: FormGroup = this.fb.group({
        name: ['', Validators.required],
        environment: ['test'], // hidden — auto-derived from build flag at save time
        baseUrl: ['', Validators.required],
        authType: ['none', Validators.required],
        token: ['']
    });

    authPanelOpen = signal(false);
    authIsEditMode = signal(false);
    authEditingId = signal<number | null>(null);

    envOptions: DropdownOption[] = [
        { label: 'Test', value: 'test' },
        { label: 'Staging', value: 'staging' },
        { label: 'Production', value: 'prod' }
    ];

    authTypeOptions: DropdownOption[] = [
        { label: 'None', value: 'none' },
        { label: 'Bearer', value: 'bearer' },
        { label: 'API Key', value: 'api_key' },
        { label: 'Basic', value: 'basic' },
        { label: 'OAuth 2.0', value: 'oauth2' },
        { label: 'Access Token', value: 'access_token' }
    ];

    headlineActionOptions: TableActionOptions = {
        showToggleButton: true,
        columnLabel: 'ACTIONS',
        toggleOptions: [
            { label: 'Edit', iconName: 'pencil', value: 'edit', color: 'text-blue-500', isShow: true },
            { label: 'Delete', iconName: 'trash', value: 'delete', color: 'text-red-500', isShow: true }
        ]
    };

    apiActionOptions: TableActionOptions = {
        showToggleButton: true,
        columnLabel: 'ACTIONS',
        toggleOptions: [
            { label: 'Edit', iconName: 'pencil', value: 'edit', color: 'text-blue-500', isShow: true },
            { label: 'Delete', iconName: 'trash', value: 'delete', color: 'text-red-500', isShow: true }
        ]
    };

    agentActionOptions: TableActionOptions = {
        showToggleButton: true,
        columnLabel: 'ACTIONS',
        toggleOptions: [
            { label: 'Edit', iconName: 'pencil', value: 'edit', color: 'text-blue-500', isShow: true },
            { label: 'Delete', iconName: 'trash', value: 'delete', color: 'text-red-500', isShow: true }
        ]
    };

    statusOptions: DropdownOption[] = [
        { label: 'All Status', value: 'all' },
        { label: 'Active', value: 'active' },
        { label: 'Inactive', value: 'inactive' }
    ];

    methodOptions: DropdownOption[] = [
        { label: 'All Methods', value: 'all' },
        { label: 'GET', value: 'GET' },
        { label: 'POST', value: 'POST' },
        { label: 'PUT', value: 'PUT' },
        { label: 'DELETE', value: 'DELETE' },
        { label: 'PATCH', value: 'PATCH' }
    ];

    applyFilters() {
        this.appliedSearch.set(this.searchQuery());
        this.appliedStatus.set(this.selectedStatus());
        this.appliedMethod.set(this.selectedMethod());
        this.appliedPage.set(this.selectedPage());
        this.appliedProduct.set(this.selectedProduct());
    }

    resetFilters() {
        this.searchQuery.set('');
        this.selectedStatus.set('all');
        this.selectedMethod.set('all');
        this.selectedPage.set('all');
        this.selectedProduct.set('all');
        this.appliedSearch.set('');
        this.appliedStatus.set('all');
        this.appliedMethod.set('all');
        this.appliedPage.set('all');
        this.appliedProduct.set('all');
    }



    addMethodOptions: DropdownOption[] = [
        { label: 'GET', value: 'GET' },
        { label: 'POST', value: 'POST' },
        { label: 'PUT', value: 'PUT' },
        { label: 'DELETE', value: 'DELETE' }
    ];

    switchTab(tab: 'headline' | 'api' | 'auth' | 'agent') {
        this.activeTab.set(tab);
        if (tab === 'auth' && this.authData().length === 0) {
            this.fetchAuthData();
        } else if (tab === 'api') {
            this.fetchApiData();
        } else if (tab === 'agent') {
            this.fetchApiData(); // Still need API data for dropdowns
            this.fetchAgentData();
        }
    }

    private fetchApiData(pageId?: number) {
        this.aiBotService.getHeadlinePageApis(pageId).subscribe({
            next: (res: any) => {
                this.apiData.set(res?.data || res || []);
            },
            error: (err) => {
                console.error('Error fetching APIs:', err);
                this.apiData.set([]);
            }
        });
    }

    private fetchProducts() {
        this.aiBotService.getProducts().subscribe({
            next: (res: any) => {
                this.productData.set(res?.data || res || []);
            },
            error: (err) => {
                console.error('Error fetching products:', err);
                this.productData.set([]);
            }
        });
    }

    handleTableAction(event: { action: string; row: any }) {
        if (event.action === 'edit') {
            if (this.activeTab() === 'auth') {
                this.openAuthEditPanel(event.row);
            } else if (this.activeTab() === 'agent') {
                this.openAgentEditPanel(event.row);
            } else {
                this.openEditPanel(event.row);
            }
        } else if (event.action === 'delete') {
            if (this.activeTab() === 'headline') {
                this.openDeleteConfirmation(event.row);
            } else if (this.activeTab() === 'auth') {
                this.openAuthDeleteConfirmation(event.row);
            } else if (this.activeTab() === 'api') {
                this.openApiDeleteConfirmation(event.row);
            } else if (this.activeTab() === 'agent') {
                this.openAgentDeleteConfirmation(event.row);
            }
        }
    }

    openApiDeleteConfirmation(row: any) {
        this.dialog.open(ConfirmationModalComponent, {
            width: '400px',
            data: {
                title: 'Confirm Deletion',
                description: `Are you sure you want to delete the API "${row.api_name}"?`
            }
        }).afterClosed().subscribe(res => {
            if (res) {
                this.handleDeleteApi(row.id);
            }
        });
    }

    handleDeleteApi(id: number) {
        const row = this.apiData().find(p => p.id === id);
        this.aiBotService.deleteHeadlinePageApi(id).subscribe({
            next: (res: any) => {
                this.auditService.createAuditLog({
                    entityType: AuditEntityType.HEADLINE_API,
                    action: AuditAction.DELETE,
                    actionTitle: `Headline API Deleted`,
                    oldValue: row || { id },
                    newValue: null,
                    note: `Deleted API endpoint '${row?.api_name || id}' [${row?.method || 'N/A'}] ${row?.base_url || ''}`
                }).subscribe();

                this.toasterService.success(
                    res?.data?.message || 'API deleted successfully',
                    'Success'
                );
                this.fetchApiData();
                this.auditLogPanel?.reloadLogs();
            },
            error: (err: any) => {
                console.error('Error deleting API:', err);
            }
        });
    }

    // Authentication CRUD Methods
    fetchAuthData() {
        this.aiBotService.getAuthConfigs().subscribe({
            next: (res: any) => {
                this.authData.set(res?.data || res || []);
            },
            error: (err) => {
                console.error('Error fetching auth configs:', err);
                this.authData.set([]);
            }
        });
    }

    fetchAgentData() {
        this.aiBotService.getAgents().subscribe({
            next: (res: any) => {
                const data = res?.data || res || [];
                this.agentData.set(data);
                this.fetchMissingFeatureOptions(data);
            },
            error: (err) => {
                console.error('Error fetching agents:', err);
                this.agentData.set([]);
            }
        });
    }

    openAgentDeleteConfirmation(row: any) {
        this.dialog.open(ConfirmationModalComponent, {
            width: '400px',
            data: {
                title: 'Confirm Deletion',
                description: `Are you sure you want to delete the Agent "${row.name}"?`
            }
        }).afterClosed().subscribe(res => {
            if (res) {
                this.handleDeleteAgent(row.id);
            }
        });
    }

    handleDeleteAgent(id: number) {
        this.aiBotService.deleteAgent(id).subscribe({
            next: (res: any) => {
                this.toasterService.success(res?.data?.message || 'Agent deleted successfully', 'Success');
                this.fetchAgentData();
            },
            error: (err) => console.error('Error deleting agent:', err)
        });
    }

    private fetchAllFeatures() {
        this.promptBotService.getFeatures().subscribe({
            next: (features) => this.allFeatures.set(features || []),
            error: (err) => console.error('Error fetching all features:', err)
        });
    }

    private fetchMissingFeatureOptions(agents: any[]) {
        const featureIds = [...new Set(agents.map(a => a.feature_id))].filter(id => id && !this.allFeatureOptionsMap()[id]);

        featureIds.forEach(id => {
            this.promptBotService.getFeatureOptions(id).subscribe({
                next: (options) => {
                    this.allFeatureOptionsMap.update(map => ({ ...map, [id]: options || [] }));
                }
            });
        });
    }

    openAuthAddPanel() {
        this.authForm.reset({
            name: '',
            environment: 'test',
            baseUrl: '',
            authType: 'none',
            token: ''
        });
        this.authIsEditMode.set(false);
        this.authEditingId.set(null);
        this.authPanelOpen.set(true);
    }

    openAuthEditPanel(row: any) {
        // Capture previous auth state for audit diff
        this.previousAuthState = { ...row };
        this.authForm.patchValue({
            name: row.name || '',
            environment: row.environment || 'test',
            baseUrl: row.base_url || '',
            authType: row.auth_type || 'none',
            token: row.token || ''
        });
        this.authIsEditMode.set(true);
        this.authEditingId.set(row.id || null);
        this.authPanelOpen.set(true);
    }

    closeAuthPanel() {
        this.authPanelOpen.set(false);
    }

    openAuthDeleteConfirmation(row: any) {
        this.dialog.open(ConfirmationModalComponent, {
            width: '400px',
            data: {
                title: 'Confirm Deletion',
                description: `Are you sure you want to delete the authentication config "${row.name}"?`
            }
        }).afterClosed().subscribe(res => {
            if (res) {
                this.handleDeleteAuth(row.id);
            }
        });
    }

    handleDeleteAuth(id: number) {
        const row = this.authData().find(p => p.id === id);
        this.aiBotService.deleteAuthConfig(id).subscribe({
            next: (res: any) => {
                this.auditService.createAuditLog({
                    entityType: AuditEntityType.HEADLINE_AUTH,
                    action: AuditAction.DELETE,
                    actionTitle: `Headline Auth Config Deleted`,
                    oldValue: row || { id },
                    newValue: null,
                    note: `Deleted auth config '${row?.name || id}' (${row?.auth_type || 'N/A'}) — ${row?.environment || ''} environment`
                }).subscribe();

                this.toasterService.success(
                    res?.data?.message || 'Authentication config deleted successfully',
                    'Success'
                );
                this.fetchAuthData();
                this.auditLogPanel?.reloadLogs();
            },
            error: (err: any) => {
                console.error('Error deleting auth config:', err);
            }
        });
    }

    saveAuthConfig() {
        if (this.authForm.invalid) {
            this.authForm.markAllAsTouched();
            return;
        }

        const formData = this.authForm.getRawValue();
        // Auto-derive environment: 'prod' on production builds, 'test' on all others
        const autoEnvironment = environment.production ? 'prod' : 'test';
        const payload = {
            product_id: Number(this.currentPid()),
            name: formData.name,
            environment: autoEnvironment,
            base_url: formData.baseUrl,
            auth_type: formData.authType,
            token: formData.token,
            is_active: true
        };

        if (this.authIsEditMode() && this.authEditingId()) {
            this.aiBotService.updateAuthConfig(this.authEditingId()!, payload).subscribe({
                next: (res: any) => {
                    // Build diff for UPDATE
                    const oldValue: any = { id: this.authEditingId() };
                    const newValue: any = { id: this.authEditingId() };
                    const fields = ['name', 'environment', 'base_url', 'auth_type'] as const;
                    fields.forEach(field => {
                        const prev = this.previousAuthState?.[field];
                        const next = payload[field as keyof typeof payload];
                        if (prev !== next) {
                            oldValue[field] = prev;
                            newValue[field] = next;
                        }
                    });

                    this.auditService.createAuditLog({
                        entityType: AuditEntityType.HEADLINE_AUTH,
                        action: AuditAction.UPDATE,
                        actionTitle: `Headline Auth Config Updated`,
                        oldValue,
                        newValue,
                        note: `Updated auth config '${payload.name}' (${payload.auth_type}) — ${payload.environment} environment`
                    }).subscribe();
                    this.previousAuthState = null;

                    this.toasterService.success(
                        res?.data?.message || 'Auth config updated successfully',
                        'Success'
                    );
                    this.closeAuthPanel();
                    this.fetchAuthData();
                    this.auditLogPanel?.reloadLogs();
                }
            });
        } else {
            this.aiBotService.addAuthConfig(payload).subscribe({
                next: (res: any) => {
                    this.auditService.createAuditLog({
                        entityType: AuditEntityType.HEADLINE_AUTH,
                        action: AuditAction.CREATE,
                        actionTitle: `Headline Auth Config Created`,
                        oldValue: null,
                        newValue: { ...payload, id: res?.data?.id || res?.id },
                        note: `Created auth config '${payload.name}' (${payload.auth_type}) — ${payload.environment} environment`
                    }).subscribe();

                    this.toasterService.success(
                        res?.data?.message || 'Auth config added successfully',
                        'Success'
                    );
                    this.closeAuthPanel();
                    this.fetchAuthData();
                    this.auditLogPanel?.reloadLogs();
                }
            });
        }
    }

    openDeleteConfirmation(row: any) {
        this.dialog.open(ConfirmationModalComponent, {
            width: '400px',
            data: {
                title: 'Confirm Deletion',
                description: `Are you sure you want to delete the headline page "${row.name}"?`
            }
        }).afterClosed().subscribe(res => {
            if (res) {
                this.handleDeleteHeadlinePage(row.id);
            }
        });
    }

    handleDeleteHeadlinePage(id: number) {
        const row = this.headlineData().find(p => p.id === id);
        this.aiBotService.deleteHeadlinePage(id).subscribe({
            next: (res: any) => {
                this.auditService.createAuditLog({
                    entityType: AuditEntityType.HEADLINE_PAGE,
                    action: AuditAction.DELETE,
                    actionTitle: `Headline Page Deleted`,
                    oldValue: row || { id },
                    newValue: null,
                    note: `Deleted headline page '${row?.name || id}' with keyname '${row?.keyname || 'N/A'}'`
                }).subscribe();

                this.toasterService.success(
                    res?.data?.message || 'Headline page deleted successfully',
                    'Success'
                );
                this.fetchHeadlinePages();
                this.auditLogPanel?.reloadLogs();
            },
            error: (err: any) => {
                console.error('Error deleting headline page:', err);
            }
        });
    }

    openAddPanel() {
        this.apiForm.reset({
            id: 0,
            product_id: Number(this.currentPid()) || null,
            headline_page_id: null,
            data_source_id: null,
            method: 'GET',
            name: '',
            keyname: '',
            description: '',
            endpoint: ''
        });
        this.payloadArray.clear();
        this.addPayloadField(); // Add one empty field by default
        this.apiForm.get('keyname')?.enable(); // Ensure keyname is enabled for new entries
        this.isEditMode.set(false);
        this.panelOpen.set(true);
    }

    openEditPanel(row: any) {
        this.isEditMode.set(true);
        this.editingId.set(row.id || null);
        this.payloadArray.clear();

        // Enable keyname by default
        this.apiForm.get('keyname')?.enable();

        if (this.activeTab() === 'headline' && row.id) {
            // Capture previous headline state for audit diff
            this.previousHeadlineState = { ...row };
            // Disable keyname for headline page edit as requested
            this.apiForm.get('keyname')?.disable();

            // Fetch latest data from API then pre-fill form
            this.aiBotService.getHeadlinePage(row.id).subscribe({
                next: (res: any) => {
                    const page = res?.data || res;
                    this.apiForm.patchValue({
                        id: page.id || row.id,
                        product_id: page.product_id || Number(this.currentPid()) || null,
                        headline_page_id: page.id || null,
                        name: page.name || '',
                        keyname: page.keyname || '',
                        description: page.description || ''
                    });
                },
                error: () => {
                    // Fallback: use row data directly
                    this.apiForm.patchValue({
                        id: row.id || 0,
                        product_id: row.product_id || Number(this.currentPid()) || null,
                        headline_page_id: row.id || null,
                        name: row.name || '',
                        keyname: row.keyname || '',
                        description: row.description || ''
                    });
                }
            });
        } else {
            // Capture previous API state for audit diff
            this.previousApiState = { ...row };
            this.apiForm.patchValue({
                id: row.id || 0,
                headline_page_id: row.headline_page_id || null,
                data_source_id: row.data_source_id || null,
                name: row.api_name || row.title || row.name || '',
                keyname: row.api_keyname || row.slug || row.keyname || '',
                description: row.description || '',
                method: row.method || 'GET',
                endpoint: row.base_url || row.endpoint || ''
            });

            if (row.config) {
                const config = row.config;
                Object.keys(config).forEach(key => {
                    this.payloadArray.push(this.fb.group({
                        key: [key],
                        value: [config[key] || '']
                    }));
                });
            } else if (row.payload && Array.isArray(row.payload)) {
                row.payload.forEach((p: any) => {
                    this.payloadArray.push(this.fb.group({
                        key: [p.key || ''],
                        value: [p.value || '']
                    }));
                });
            }
        }

        this.panelOpen.set(true);
    }

    addPayloadField() {
        this.payloadArray.push(this.fb.group({
            key: [''],
            value: ['']
        }));
    }

    removePayloadField(index: number) {
        this.payloadArray.removeAt(index);
    }

    closePanel() {
        this.panelOpen.set(false);
    }

    adjustId(delta: number) {
        const currentId = this.apiForm.get('id')?.value || 0;
        this.apiForm.patchValue({ id: Math.max(0, currentId + delta) });
    }

    save() {
        if (this.apiForm.valid) {
            const formData = this.apiForm.getRawValue();

            if (this.activeTab() === 'headline') {
                if (this.isEditMode() && this.editingId()) {
                    // Update existing headline page
                    const updatePayload = {
                        product_id: Number(formData.product_id),
                        name: formData.name,
                        description: formData.description || '',
                        is_active: true
                    };

                    this.aiBotService.updateHeadlinePage(this.editingId()!, updatePayload).subscribe({
                        next: (response: any) => {
                            // Build diff for UPDATE
                            const oldValue: any = { id: this.editingId() };
                            const newValue: any = { id: this.editingId() };
                            const fields = ['name', 'description'] as const;
                            fields.forEach(field => {
                                const prev = this.previousHeadlineState?.[field];
                                const next = updatePayload[field as keyof typeof updatePayload];
                                if (prev !== next) {
                                    oldValue[field] = prev;
                                    newValue[field] = next;
                                }
                            });

                            this.auditService.createAuditLog({
                                entityType: AuditEntityType.HEADLINE_PAGE,
                                action: AuditAction.UPDATE,
                                actionTitle: `Headline Page Updated`,
                                oldValue,
                                newValue,
                                note: `Updated headline page '${updatePayload.name}'`
                            }).subscribe();
                            this.previousHeadlineState = null;

                            this.toasterService.success(
                                response?.data?.message || 'Headline page updated successfully',
                                'Success'
                            );
                            this.closePanel();
                            this.fetchHeadlinePages();
                            this.auditLogPanel?.reloadLogs();
                        },
                        error: (err: any) => {
                            console.error('Error updating headline page:', err);
                        }
                    });
                } else {
                    // Create new headline page
                    const createPayload = {
                        product_id: Number(formData.product_id) || Number(this.currentPid()),
                        name: formData.name,
                        keyname: formData.keyname,
                        description: formData.description || '',
                        is_active: true
                    };

                    this.aiBotService.addHeadlinePage(createPayload).subscribe({
                        next: (response: any) => {
                            this.auditService.createAuditLog({
                                entityType: AuditEntityType.HEADLINE_PAGE,
                                action: AuditAction.CREATE,
                                actionTitle: `Headline Page Created`,
                                oldValue: null,
                                newValue: { ...createPayload, id: response?.data?.id || response?.id },
                                note: `Created headline page '${createPayload.name}' with keyname '${createPayload.keyname}'`
                            }).subscribe();

                            this.toasterService.success(
                                response?.data?.message || 'Headline page added successfully',
                                'Success'
                            );
                            this.closePanel();
                            this.fetchHeadlinePages();
                            this.auditLogPanel?.reloadLogs();
                        },
                        error: (err: any) => {
                            console.error('Error adding headline page:', err);
                        }
                    });
                }
            } else {
                // Create/Update API
                const config: Record<string, string> = {};
                formData.payload?.forEach((p: any) => {
                    if (p.key) config[p.key] = p.value || '';
                });

                const payload = {
                    headline_page_id: Number(formData.headline_page_id),
                    data_source_id: Number(formData.data_source_id),
                    api_name: formData.name,
                    api_keyname: formData.keyname,
                    description: formData.description || '',
                    base_url: formData.endpoint,
                    method: formData.method,
                    is_active: true,
                    config: config
                };

                if (this.isEditMode() && this.editingId()) {
                    // Update API
                    this.aiBotService.updateHeadlinePageApi(this.editingId()!, payload).subscribe({
                        next: (response: any) => {
                            // Build diff for UPDATE
                            const oldValue: any = { id: this.editingId() };
                            const newValue: any = { id: this.editingId() };
                            const fields = ['api_name', 'base_url', 'method', 'api_keyname', 'description'] as const;
                            fields.forEach(field => {
                                const prev = this.previousApiState?.[field];
                                const next = payload[field as keyof typeof payload];
                                if (String(prev) !== String(next)) {
                                    oldValue[field] = prev;
                                    newValue[field] = next;
                                }
                            });

                            this.auditService.createAuditLog({
                                entityType: AuditEntityType.HEADLINE_API,
                                action: AuditAction.UPDATE,
                                actionTitle: `Headline API Updated`,
                                oldValue,
                                newValue,
                                note: `Updated API endpoint '${payload.api_name}' [${payload.method}] ${payload.base_url}`
                            }).subscribe();
                            this.previousApiState = null;

                            this.toasterService.success(
                                response?.data?.message || 'API updated successfully',
                                'Success'
                            );
                            this.closePanel();
                            this.fetchApiData();
                            this.auditLogPanel?.reloadLogs();
                        },
                        error: (err: any) => {
                            console.error('Error updating API:', err);
                        }
                    });
                } else {
                    // Create new API
                    this.aiBotService.addHeadlinePageApi(payload).subscribe({
                        next: (response: any) => {
                            this.auditService.createAuditLog({
                                entityType: AuditEntityType.HEADLINE_API,
                                action: AuditAction.CREATE,
                                actionTitle: `Headline API Created`,
                                oldValue: null,
                                newValue: { ...payload, id: response?.data?.id || response?.id },
                                note: `Created API endpoint '${payload.api_name}' [${payload.method}] ${payload.base_url}`
                            }).subscribe();

                            this.toasterService.success(
                                response?.data?.message || 'API added successfully',
                                'Success'
                            );
                            this.closePanel();
                            this.fetchApiData();
                            this.auditLogPanel?.reloadLogs();
                        },
                        error: (err: any) => {
                            console.error('Error adding API:', err);
                        }
                    });
                }
            }
        }
    }

    getStatusClass(status: string): string {
        if (!status) return 'bg-slate-100 text-slate-700 dark:bg-slate-500/20 dark:text-slate-400';
        switch (status.toLowerCase()) {
            case 'active':
            case 'enabled':
                return 'bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-400';
            case 'draft':
                return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-500/20 dark:text-yellow-400';
            case 'disabled':
                return 'bg-slate-100 text-slate-700 dark:bg-slate-500/20 dark:text-slate-400';
            default:
                return 'bg-slate-100 text-slate-700 dark:bg-slate-500/20 dark:text-slate-400';
        }
    }

    getMethodClass(method: string): string {
        if (!method) return 'bg-slate-100 text-slate-700 dark:bg-slate-500/20 dark:text-slate-400';
        switch (method.toUpperCase()) {
            case 'GET':
                return 'bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-400';
            case 'POST':
                return 'bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-400';
            case 'PUT':
                return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-500/20 dark:text-yellow-400';
            case 'DELETE':
                return 'bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-400';
            default:
                return 'bg-slate-100 text-slate-700 dark:bg-slate-500/20 dark:text-slate-400';
        }
    }

    // Agent Management Methods
    openAgentAddPanel() {
        this.agentForm.reset({
            product_id: null,
            feature_id: null,
            feature_option_id: null,
            page_ids: null,
            api_ids: [],
            name: '',
            knowledge_base_collection: ''
        });
        this.agentForm.get('product_id')?.enable();
        this.agentForm.get('feature_id')?.enable();
        this.agentForm.get('feature_option_id')?.enable();
        this.agentForm.get('page_ids')?.enable();
        this.agentForm.get('api_ids')?.enable();

        this.features.set([]);
        this.featureOptions.set([]);
        this.agentIsEditMode.set(false);
        this.agentEditingId.set(null);
        this.agentPanelOpen.set(true);
    }

    openAgentEditPanel(row: any) {
        this.agentIsEditMode.set(true);
        this.agentEditingId.set(row.id || null);

        // Fetch dependent data to populate dropdown options
        if (row.product_id) {
            this.fetchHeadlinePages(Number(row.product_id));
            this.promptBotService.getMappings(Number(row.product_id)).subscribe({
                next: (res: any) => {
                    const mappings = res?.data || res || [];
                    const features = mappings
                        .filter((m: any) => m.feature && m.is_active)
                        .map((m: any) => m.feature);
                    this.features.set(features);
                }
            });
        }

        if (row.feature_id) {
            this.promptBotService.getFeatureOptions(Number(row.feature_id)).subscribe({
                next: (res: any) => {
                    this.featureOptions.set(res?.data || res || []);
                }
            });
        }

        if (row.selected_page_ids && row.selected_page_ids.length > 0) {
            this.fetchApiData(Number(row.selected_page_ids[0]));
        }

        this.agentForm.reset({
            product_id: String(row.product_id || ''),
            feature_id: String(row.feature_id || ''),
            feature_option_id: String(row.feature_option_id || ''),
            page_ids: row.selected_page_ids && row.selected_page_ids.length > 0 ? String(row.selected_page_ids[0]) : null,
            api_ids: row.selected_api_ids ? row.selected_api_ids.map(String) : [],
            name: row.name || '',
            knowledge_base_collection: row.collection_name || ''
        });

        // Disable immutable fields, but keep APIs and Pages editable
        this.agentForm.get('product_id')?.disable();
        this.agentForm.get('feature_id')?.disable();
        this.agentForm.get('feature_option_id')?.disable();

        this.agentForm.get('page_ids')?.enable();
        this.agentForm.get('api_ids')?.enable();

        this.agentPanelOpen.set(true);
    }


    closeAgentPanel() {
        this.agentPanelOpen.set(false);
    }

    onAgentProductChange(productId: any) {
        if (!productId || productId === 'all') {
            this.features.set([]);
            this.featureOptions.set([]);
            this.fetchHeadlinePages(); // Fetch all pages
            return;
        }

        // Reset downstream selections
        this.agentForm.patchValue({ page_ids: null, api_ids: [] });

        // Fetch filtered pages
        this.fetchHeadlinePages(Number(productId));

        this.promptBotService.getMappings(Number(productId)).subscribe({
            next: (res: any) => {
                const mappings = res?.data || res || [];
                const features = mappings
                    .filter((m: any) => m.feature && m.is_active)
                    .map((m: any) => m.feature);
                this.features.set(features);
                this.agentForm.patchValue({ feature_id: null, feature_option_id: null });
                this.featureOptions.set([]);
            },
            error: (err) => {
                console.error('Error fetching features:', err);
                this.features.set([]);
            }
        });
    }

    onAgentFeatureChange(featureId: any) {
        if (!featureId) {
            this.featureOptions.set([]);
            return;
        }

        this.promptBotService.getFeatureOptions(Number(featureId)).subscribe({
            next: (res: any) => {
                this.featureOptions.set(res?.data || res || []);
                this.agentForm.patchValue({ feature_option_id: null });
            },
            error: (err) => {
                console.error('Error fetching feature options:', err);
                this.featureOptions.set([]);
            }
        });
    }

    onAgentPageChange(pageId: any) {
        if (!pageId) {
            this.fetchApiData();
            return;
        }
        this.fetchApiData(Number(pageId));
        // Clear selected APIs because they might not be relevant to the new page
        this.agentForm.patchValue({ api_ids: [] });
    }

    saveAgent() {
        if (this.agentForm.invalid) {
            this.agentForm.markAllAsTouched();
            return;
        }

        const formData = this.agentForm.getRawValue();

        if (this.agentIsEditMode() && this.agentEditingId()) {
            const payload = {
                name: formData.name,
                selected_page_ids: Array.isArray(formData.page_ids)
                    ? formData.page_ids.map((id: any) => Number(id))
                    : (formData.page_ids ? [Number(formData.page_ids)] : []),
                selected_api_ids: (formData.api_ids || []).map((id: any) => Number(id))
            };

            this.aiBotService.updateAgent(this.agentEditingId()!, payload).subscribe({
                next: (res: any) => {
                    this.toasterService.success(res?.data?.message || 'Agent updated successfully', 'Success');
                    this.fetchAgentData();
                    this.closeAgentPanel();
                },
                error: (err) => console.error('Error updating agent:', err)
            });
        } else {
            const payload = {
                name: formData.name,
                product_id: Number(formData.product_id),
                feature_id: Number(formData.feature_id),
                feature_option_id: Number(formData.feature_option_id),
                selected_page_ids: Array.isArray(formData.page_ids)
                    ? formData.page_ids.map((id: any) => Number(id))
                    : (formData.page_ids ? [Number(formData.page_ids)] : []),
                selected_api_ids: (formData.api_ids || []).map((id: any) => Number(id)),
                collection_name: formData.knowledge_base_collection || ''
            };

            this.aiBotService.setupAgent(payload).subscribe({
                next: (res: any) => {
                    this.toasterService.success(res?.data?.message || 'Agent setup successfully', 'Success');
                    this.fetchAgentData();
                    this.closeAgentPanel();
                    this.auditLogPanel?.reloadLogs();
                },
                error: (err) => console.error('Error setting up agent:', err)
            });
        }
    }
}
